import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Grid,
    GridItem,
    Icon,
    Input,
    InputGroup,
    InputRightElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useToast,
} from "@chakra-ui/react";
import React, {useEffect, useState} from "react";
import TextInput from "../formFields/TextInput.tsx";
import {useCreateUserMutation, useUpdateUserMutation} from "../../store/userApi/userApiSlice.ts";
import type {createUserType, user} from "../../utils/typesConstant/types";
import {RiEyeCloseLine} from "react-icons/ri";
import {MdOutlineRemoveRedEye} from "react-icons/md";


interface AddUserModalProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    userDetails?: user;
    setUserDetails?: (userDetails: user) => void;
}

export default function AddUserModal(props: AddUserModalProps) {
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [show, setShow] = useState(false);
    const handleClick = () => setShow(!show);
    const [addUserFormData, setAddUserFormData] = useState<user>({
        id: 0,
        date_joined: "",
        groups: [],
        is_active: false,
        is_staff: false,
        is_superuser: false,
        last_login: undefined,
        user_permissions: [],
        username: "",
        password: "",
        email: "",
        first_name: "",
        last_name: ""
    });

    const [addUser] = useCreateUserMutation();
    const [updateUser] = useUpdateUserMutation();

    useEffect(() => {
        if (props.userDetails) {
            setAddUserFormData({
                ...props.userDetails,
                password: ""
            });
        } else {
            setAddUserFormData({
                date_joined: "",
                groups: [],
                is_active: false,
                is_staff: false,
                is_superuser: false,
                last_login: undefined,
                user_permissions: [],
                id: 0,
                username: "",
                password: "",
                email: "",
                first_name: "",
                last_name: ""
            });
        }
    }, [props.userDetails]);

    const handleClose = () => {
        props.setUserDetails?.({} as user);
        props.setOpen(false);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        setIsLoading(true);
        event.preventDefault();
        const formData = new FormData(event.currentTarget);

        // ✅ Safer and more explicit version
        const formJson = {
            username: formData.get("username") as string,
            password: formData.get("password") as string,
            first_name: formData.get("first_name") as string,
            last_name: formData.get("last_name") as string,
            email: formData.get("email") as string,
        };

        try {
            if (props.userDetails?.id) {
                const updatedUserJson: user = {
                    ...props.userDetails,
                    username: formJson.username,
                    password: formJson.password,
                    first_name: formJson.first_name,
                    last_name: formJson.last_name,
                    email: formJson.email,
                };

                await updateUser(updatedUserJson).unwrap();

                toast({
                    title: "User updated successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                await addUser(formJson as createUserType).unwrap();

                toast({
                    title: "User created successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            }
            setIsLoading(() => false)
            handleClose();
        } catch (error: any) {
            toast({
                title: "Operation failed.",
                description: error?.data?.message || "Please try again.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        }
    };

    return (
        <Modal isOpen={props.open} onClose={handleClose} size="xl">
            <ModalOverlay/>
            <ModalContent as="form" onSubmit={handleSubmit}>
                <ModalHeader fontSize="xl" fontWeight="bold">
                    {props.userDetails?.id ? "Update User" : "Create New User"}
                </ModalHeader>

                <ModalCloseButton/>
                <ModalBody>
                    <Box>
                        <Grid templateColumns="repeat(12, 1fr)" gap={4} mb={4}>
                            <GridItem colSpan={6}>
                                <TextInput
                                    id="username_field"
                                    label="Username"
                                    value={addUserFormData.username}
                                    name="username"
                                    formObject={addUserFormData}
                                    setFormObject={setAddUserFormData}
                                />
                            </GridItem>
                            {!props.userDetails?.id ? <GridItem colSpan={6}>
                                {/*<TextInput*/}
                                {/*    id="password_field"*/}
                                {/*    label="Password"*/}
                                {/*    value={addUserFormData.password}*/}
                                {/*    name="password"*/}
                                {/*    formObject={addUserFormData}*/}
                                {/*    setFormObject={setAddUserFormData}*/}
                                {/*/>*/}
                                {/*<InputRightElement display="flex" alignItems="center" mt="4px">*/}
                                {/*    <Icon*/}
                                {/*        color={textColorSecondary}*/}
                                {/*        _hover={{ cursor: "pointer" }}*/}
                                {/*        as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}*/}
                                {/*        onClick={handleClick}*/}
                                {/*    />*/}
                                {/*</InputRightElement>*/}
                                <FormControl id="password_field">
                                    <FormLabel>Password</FormLabel>
                                    <InputGroup>
                                        <Input
                                            type={show ? "text" : "password"}
                                            name="password"
                                            value={addUserFormData.password}
                                            onChange={(e) =>
                                                setAddUserFormData({...addUserFormData, password: e.target.value})
                                            }
                                        />
                                        <InputRightElement h="full">
                                            <Icon
                                                as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                                                onClick={handleClick}
                                                cursor="pointer"
                                                color="gray.500"
                                            />
                                        </InputRightElement>
                                    </InputGroup>
                                </FormControl>
                            </GridItem> : null}
                        </Grid>

                        <Grid templateColumns="repeat(12, 1fr)" gap={4} mb={4}>
                            <GridItem colSpan={6}>
                                <TextInput
                                    id="first_name_field"
                                    label="First Name"
                                    value={addUserFormData.first_name}
                                    name="first_name"
                                    formObject={addUserFormData}
                                    setFormObject={setAddUserFormData}
                                />
                            </GridItem>
                            <GridItem colSpan={6}>
                                <TextInput
                                    id="last_name_field"
                                    label="Last Name"
                                    value={addUserFormData.last_name}
                                    name="last_name"
                                    formObject={addUserFormData}
                                    setFormObject={setAddUserFormData}
                                />
                            </GridItem>
                        </Grid>

                        <Grid templateColumns="repeat(12, 1fr)" gap={4}>
                            <GridItem colSpan={12}>
                                <TextInput
                                    id="email_field"
                                    label="Email"
                                    value={addUserFormData.email}
                                    name="email"
                                    formObject={addUserFormData}
                                    setFormObject={setAddUserFormData}
                                />
                            </GridItem>
                        </Grid>
                    </Box>
                </ModalBody>

                <ModalFooter>
                    <Button onClick={handleClose} mr={3}>
                        Cancel
                    </Button>
                    <Button type="submit" colorScheme="blue" isLoading={isLoading}>
                        {props.userDetails?.id ? "Update" : "Create"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
